import type { CSSProperties } from 'react';
import { BRAND, PRODUCT_NAME } from '../brand.js';
import { can, isFirmRole } from '@domain';
import type { Organisation } from '../api.js';
import { signOutUrl } from '../auth.js';
import { GROUPS, type EnabledFeatures } from '../data/forms.js';
import type { Translate } from '../i18n/index.js';
import { connectionStyle, theme } from '../theme.js';
import type { Action, AppState, Role } from '../state.js';
import { Icon } from './Icon.js';

interface SidebarProps {
  state: AppState;
  screen: string;
  organisations: Organisation[];
  active: Organisation | undefined;
  role: Role;
  /** Which optional sections this company was given. */
  features: EnabledFeatures;
  /** The signed-in person, for the block at the foot of the sidebar. */
  user: { email: string | null; displayName: string | null };
  rtl: boolean;
  t: Translate;
  dispatch: (action: Action) => void;
  onSelectOrganisation: (orgId: number) => void;
  /** True while the server is being told; a second choice now would race it. */
  switching?: boolean;
}

function navItemStyle(active: boolean, expanded: boolean): CSSProperties {
  const shared: CSSProperties = {
    border: 0,
    borderRadius: theme.radiusPill,
    cursor: 'pointer',
    background: active ? theme.ink : 'transparent',
    color: active ? '#fff' : theme.navText,
    boxShadow: active ? theme.shadowNavActive : 'none',
  };
  if (!expanded) {
    return {
      ...shared,
      display: 'grid',
      placeItems: 'center',
      width: '40px',
      height: '40px',
      margin: '0 auto',
      position: 'relative',
    };
  }
  return {
    ...shared,
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    width: '100%',
    padding: '10px 8px',
    textAlign: 'start',
    fontSize: '13.5px',
    fontWeight: active ? 600 : 400,
  };
}

/**
 * Two letters for the avatar, taken from whatever the identity provider gave
 * us. A display name is preferred; an email address is the usual fallback,
 * since Entra External ID does not always carry a name.
 */
function initialsOf(user: { email: string | null; displayName: string | null }): string {
  const source = user.displayName ?? user.email?.split('@')[0] ?? '';
  const letters = source
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part[0]!)
    .slice(0, 2)
    .join('');
  return letters.toUpperCase() || '?';
}

/** What this person is, in this company. Their role can differ in the next one. */
const ROLE_LABELS = {
  cfoai_admin: 'CFO · Admin',
  cfoai_staff: 'CFO · Staff',
  client_admin: 'Organisation admin',
  client_user: 'Data entry',
} as const;

export function Sidebar({
  state,
  screen,
  organisations,
  active: activeOrg,
  role,
  features,
  user,
  rtl,
  t,
  dispatch,
  onSelectOrganisation,
  switching = false,
}: SidebarProps) {
  const expanded = !state.collapsed;
  /*
    Switching company and reaching the practice's own screens belong to CFOAI.
    A client_admin runs their own books but holds exactly one company, so an
    organisation picker would be a menu of one.
  */
  const isFirm = isFirmRole(role);

  const groups = GROUPS.map((group) => ({
    ...group,
    label: t(group.key.charAt(0) + group.key.slice(1).toLowerCase()).toUpperCase(),
    // Shown when this person could actually use it. What is permitted is
    // decided again by the API, from this same table.
    items: group.items.filter(
      (item) =>
        (!item.needs || can(role, item.needs)) &&
        // A section this company was never given. The whole group drops
        // out below when nothing in it survives, so a client without
        // Training does not get an empty TRAINING heading.
        (!item.feature || features[item.feature]),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <nav
      style={{
        flex: '0 0 auto',
        width: expanded ? '252px' : '76px',
        background: theme.nav,
        /*
          Square, so the rail runs the full height of the window edge to
          edge. The rounded inner corner it used to carry made it read as
          a card floating on the canvas rather than as the side of the
          application, and left a sliver of grey at the corners.
        */
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width .18s ease',
      }}
    >
      {/*
        The logo.

        The full lockup already contains the mark, so the two are swapped
        rather than stacked — collapsed, only the mark fits in 76px;
        expanded, the lockup says the name properly.

        An image rather than CSS: this is a logo with its own letterforms
        and two colours, and rebuilding it would be a drawing of a logo
        rather than the logo. The name rides in `alt`, so it is still
        readable to a screen reader or with images off.

        The row follows the page direction, so the logo hugs the side the
        reader starts from — the right in Arabic — like everything else
        in the sidebar. The artwork itself never mirrors: `dir` turns
        text around, not images, so forcing LTR here only pinned the logo
        to the wrong edge in Arabic.
      */}
      <div
        style={{
          padding: expanded ? '22px 18px 14px' : '22px 0 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
        }}
      >
        <img
          src={expanded ? BRAND.lockupDark : BRAND.mark}
          alt={PRODUCT_NAME}
          style={{
            display: 'block',
            // The lockup is wide and short; the mark is square. Sizing
            // each by height keeps them optically the same weight.
            height: expanded ? '42px' : '36px',
            width: 'auto',
            maxWidth: '100%',
          }}
        />
      </div>

      {expanded && organisations.length > 0 && (
        <div style={{ padding: '0 14px 16px' }}>
          <button
            onClick={() => isFirm && dispatch({ type: 'toggleOrgMenu' })}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              background: theme.navRaised,
              border: '1px solid #3A344480',
              borderRadius: theme.radiusControl,
              padding: '11px 12px',
              color: theme.navTextBright,
              fontSize: '13.5px',
              fontWeight: 500,
              cursor: isFirm ? 'pointer' : 'default',
              textAlign: 'start',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.good} strokeWidth="2.2">
              <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.4" />
            </svg>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                // Named plainly when nothing is open, rather than borrowing a
                // company's name for a company nobody chose.
                color: activeOrg ? theme.navTextBright : theme.navTextDim,
              }}
            >
              {activeOrg ? t(activeOrg.name) : t('Choose a company')}
            </span>
            {isFirm && (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke={theme.good}
                strokeWidth="2.4"
                style={{ transform: state.orgOpen ? 'rotate(180deg)' : 'none' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            )}
          </button>

          {isFirm && state.orgOpen && (
            <div
              style={{
                marginTop: '7px',
                background: theme.navDeep,
                border: `1px solid ${theme.navBorder}`,
                borderRadius: theme.radiusControl,
                padding: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <div
                style={{
                  padding: '7px 9px 5px',
                  color: '#5A6676',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.13em',
                  textTransform: 'uppercase',
                }}
              >
                {t('Your client organisations')}
              </div>
              {organisations.map((option) => {
                const conn = connectionStyle(option.state);
                const isActive = option.orgId === activeOrg?.orgId;
                return (
                  <button
                    key={option.orgId}
                    disabled={switching}
                    onClick={() => onSelectOrganisation(option.orgId)}
                    title={`${t(option.name)}${option.state === 'OK' ? '' : ` — ${t(conn.label)}`}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '9px',
                      width: '100%',
                      background: isActive ? theme.navRaised : 'transparent',
                      border: 0,
                      borderRadius: '10px',
                      padding: '9px',
                      cursor: switching ? 'progress' : 'pointer',
                      opacity: switching && !isActive ? 0.55 : 1,
                      color: isActive ? '#fff' : theme.navText,
                      fontSize: '12.5px',
                      fontWeight: isActive ? 600 : 400,
                      textAlign: 'start',
                    }}
                  >
                    <span
                      style={{
                        flex: '0 0 auto',
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background: conn.dot,
                        marginTop: '5px',
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                      <span style={{ maxWidth: '100%', textAlign: 'start', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t(option.name)}
                      </span>
                      {option.state !== 'OK' && (
                        <span
                          style={{
                            fontSize: '9.5px',
                            fontWeight: 700,
                            letterSpacing: '.04em',
                            color: conn.dot,
                            background: theme.nav,
                            border: `1px solid ${conn.dot}55`,
                            borderRadius: theme.radiusPill,
                            padding: '2px 7px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t(conn.label)}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
              <div style={{ padding: '7px 9px 5px', color: theme.navTextDim, fontSize: '11px', lineHeight: 1.5 }}>
                {t(
                  'Scoped by your entitlement on the server. Switching reloads every screen against that organisation only.',
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '2px 10px 16px' }}>
        {groups.map((group) => {
          const open = expanded ? Boolean(state.openGroups[group.key]) : true;
          return (
            <div key={group.key} style={{ marginBottom: '2px' }}>
              {expanded ? (
                <button
                  onClick={() => dispatch({ type: 'toggleGroup', key: group.key })}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'transparent',
                    border: 0,
                    padding: '11px 8px',
                    cursor: 'pointer',
                    color: theme.navText,
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '.13em',
                    textTransform: 'uppercase',
                  }}
                >
                  <span style={{ flex: 1, textAlign: 'start' }}>{group.label}</span>
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ transform: open ? 'rotate(180deg)' : 'none' }}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              ) : (
                <div style={{ height: '1px', background: theme.navBorder, margin: '9px 12px' }} />
              )}

              {open && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingBottom: '8px' }}>
                  {group.items.map((item) => {
                    const active = screen === item.screen;
                    /*
                      Counted, not declared. The queue badge is the length of
                      the queue; every other item used to carry a number
                      somebody typed into the navigation table, which was the
                      same for every company and true for none.
                    */
                    const count =
                      item.screen === 'queue' && state.queue.length > 0
                        ? String(state.queue.length)
                        : undefined;
                    return (
                      <button
                        key={item.screen}
                        onClick={() => dispatch({ type: 'go', screen: item.screen })}
                        title={t(item.label)}
                        style={navItemStyle(active, expanded)}
                      >
                        <span style={{ flex: '0 0 auto', width: '18px', display: 'grid', placeItems: 'center', opacity: 0.85 }}>
                          <Icon kind={item.icon} color={active ? '#fff' : '#7A8694'} />
                        </span>
                        {expanded && (
                          <span style={{ flex: 1, textAlign: 'start', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t(item.label)}
                          </span>
                        )}
                        {count &&
                          (expanded ? (
                            <span
                              style={{
                                flex: '0 0 auto',
                                fontFamily: theme.fontMono,
                                fontSize: '11px',
                                color: theme.navTextDim,
                                background: theme.navDeep,
                                borderRadius: theme.radiusPill,
                                padding: '2px 7px',
                              }}
                            >
                              {count}
                            </span>
                          ) : (
                            <span
                              style={{
                                position: 'absolute',
                                top: '2px',
                                insetInlineEnd: 0,
                                minWidth: '16px',
                                height: '16px',
                                fontFamily: theme.fontMono,
                                fontSize: '10px',
                                lineHeight: '16px',
                                textAlign: 'center',
                                color: '#fff',
                                background: theme.ink,
                                borderRadius: theme.radiusPill,
                              }}
                            >
                              {count}
                            </span>
                          ))}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ padding: '12px 14px 10px', display: 'flex' }}>
        <button
          onClick={() => dispatch({ type: 'toggleCollapse' })}
          title={t('Collapse')}
          style={{
            width: '36px',
            height: '36px',
            marginInlineStart: expanded ? 'auto' : 0,
            marginInlineEnd: expanded ? 0 : 'auto',
            display: 'grid',
            placeItems: 'center',
            background: theme.navRaised,
            border: 0,
            borderRadius: '12px',
            color: theme.navTextDim,
            cursor: 'pointer',
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            style={{ transform: expanded === rtl ? 'rotate(180deg)' : 'none' }}
          >
            <path d="M14 6l-6 6 6 6" />
            <path d="M19 6v12" />
          </svg>
        </button>
      </div>

      <div
        style={{
          borderTop: `1px solid ${theme.navBorder}`,
          padding: '14px',
          display: 'flex',
          flexDirection: expanded ? 'row' : 'column',
          alignItems: 'center',
          gap: expanded ? '11px' : '10px',
        }}
      >
        <div
          style={{
            flex: '0 0 auto',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            // The dark gold, not the beige: white on beige-gold is 2.6 : 1
            // and unreadable. See the note at the top of theme.ts.
            background: theme.ink,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: '12px',
          }}
        >
          {initialsOf(user)}
        </div>
        {expanded && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                color: theme.navTextBright,
                fontSize: '12.5px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user.displayName ?? user.email ?? t('Signed in')}
            </div>
            {activeOrg && (
              <div style={{ color: theme.navTextDim, fontSize: '11.5px' }}>
                {t(ROLE_LABELS[activeOrg.role])}
              </div>
            )}
          </div>
        )}

        {/*
          Leaving is as much a part of the service as arriving, so this stays
          reachable whether the sidebar is open or shut. The design hides it
          with the rest of the name block when collapsed; someone who cannot
          find how to sign out does not conclude that they are safe.
        */}
        <a
          href={signOutUrl()}
          title={t('Sign out')}
          aria-label={t('Sign out')}
          style={{
            flex: '0 0 auto',
            width: '32px',
            height: '32px',
            border: 0,
            borderRadius: '12px',
            background: theme.navRaised,
            color: theme.navTextDim,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = theme.ink;
            event.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = theme.navRaised;
            event.currentTarget.style.color = theme.navTextDim;
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            style={{ transform: rtl ? 'scaleX(-1)' : 'none' }}
            aria-hidden="true"
          >
            <path d="M10 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3" />
            <path d="M15 8l4 4-4 4" />
            <path d="M19 12H9" />
          </svg>
        </a>
      </div>
    </nav>
  );
}
